import { useState } from 'react';
import { StyleSheet, View, Text, Pressable } from 'react-native';
import { Wrap } from 'react-native-flex-layout';
import { Feather } from '@expo/vector-icons';

import LegendItem from './LegendItem';
import { useTranslation } from '../i18n';

const DEFAULT_VISIBLE = 5;

/**
 * LegendSection - Displays chart legend with optional show more/less toggle
 *
 * Can be used in two modes:
 * 1. Controlled: Pass showAll, onToggle, and totalCount props
 * 2. Uncontrolled: Just pass legendItems and it manages its own state
 *
 * @param {Array} legendItems - Array of { title, color }
 * @param {number} totalCount - Total number of items (for controlled mode)
 * @param {boolean} showAll - Whether to show all items (controlled mode)
 * @param {function} onToggle - Toggle callback (controlled mode)
 */
const LegendSection = ({ legendItems, totalCount, showAll: controlledShowAll, onToggle }) => {
  const { t } = useTranslation('common');

  // Internal state for uncontrolled mode
  const [internalShowAll, setInternalShowAll] = useState(false);

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = onToggle !== undefined && onToggle !== null;
  const showAll = isControlled ? controlledShowAll : internalShowAll;
  const total = totalCount || legendItems.length;
  const hasMore = total > DEFAULT_VISIBLE;

  // In uncontrolled mode, slice the legend items
  const displayItems = isControlled
    ? legendItems // Already sliced by parent
    : (showAll || !hasMore ? legendItems : legendItems.slice(0, DEFAULT_VISIBLE));

  const handleToggle = () => {
    if (isControlled) {
      onToggle();
    } else {
      setInternalShowAll(!internalShowAll);
    }
  };

  return (
    <View style={styles.container}>
      <Wrap shouldWrapChildren="true" spacing={8}>
        {displayItems.map((legendItem, index) => (
          <LegendItem key={index} title={legendItem.title} color={legendItem.color} />
        ))}
      </Wrap>
      {hasMore && (
        <Pressable
          onPress={handleToggle}
          style={({ pressed, hovered, focused }) => [
            styles.toggleButton,
            { cursor: 'pointer' },
            (hovered || focused) && styles.toggleButtonHovered,
            pressed && styles.toggleButtonPressed,
          ]}
        >
          <Text style={styles.toggleText}>
            {showAll ? t('state.showLess') : t('state.showAllCount', { count: total })}
          </Text>
          <Feather
            name={showAll ? 'chevron-up' : 'chevron-down'}
            size={16}
            color="#6B7280"
          />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // V3: Legend container with consistent spacing
  container: {
    backgroundColor: 'white',
    marginTop: 12,
    paddingTop: 8,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
  },
  toggleButtonHovered: {
    backgroundColor: '#ECECEC',
  },
  toggleButtonPressed: {
    backgroundColor: '#E5E5E5',
  },
  toggleText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6B7280',
    marginRight: 4,
  },
});

export default LegendSection;
